/**
 * ai-assistant shared helpers — the "module not mounted" probe + the answer-status → Badge tone/glyph
 * mapping. The ai-assistant router IS mounted on a local install (deployClass hybrid), so in practice
 * the screens degrade INTERNALLY (the server returns a tagged {status:'disabled'} when no AI key is
 * configured). But per the cloud-only contract we ALSO treat a 404 / NOT_FOUND from the endpoint —
 * which is what a client probe sees when the module is genuinely un-mounted on an install — as a
 * clean "indisponible sur cette installation" state rather than a hard error.
 *
 * Every status chip combines COLOR + GLYPH + TEXT (daltonisme + médico-légal): never color alone.
 */
import type { ApiError } from "@/api";
import type { BadgeTone } from "@/ui";
import type { AnswerStatus } from "./types";

/**
 * True when an error means the endpoint is absent on this install (the module was not mounted): an
 * HTTP 404 or the server's NOT_FOUND code. Distinguished from a transient error so the screen can
 * render the "not available on this install" state instead of a retry-able ErrorState.
 */
export function isNotMounted(err: unknown): boolean {
  const e = err as Partial<ApiError> | null | undefined;
  if (!e) return false;
  return e.httpStatus === 404 || e.code === "NOT_FOUND";
}

/** Visual descriptor for an answer status — tone + glyph (shape carries meaning, not color alone). */
export interface StatusVisual {
  tone: BadgeTone;
  glyph: string;
  /** i18n key for the chip label. */
  labelKey: string;
}

const STATUS_VISUAL: Record<AnswerStatus, StatusVisual> = {
  ok: { tone: "success", glyph: "●", labelKey: "ai.status.ok" },
  abstenu: { tone: "warn", glyph: "▲", labelKey: "ai.status.abstenu" },
  disabled: { tone: "neutral", glyph: "○", labelKey: "ai.status.disabled" },
  error: { tone: "danger", glyph: "■", labelKey: "ai.status.error" },
};

export function statusVisual(status: AnswerStatus): StatusVisual {
  return STATUS_VISUAL[status] ?? STATUS_VISUAL.error;
}
